import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Home, BookOpen } from "lucide-react";
import "../../css/nnat/nnat.css";
import Header from '../Header';
import Footer from '../Footer';
import FAQNNAT from "./FAQNNAT";
import NnatExamples from "./NnatExamples";

export default function NnatPage() {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate("/", { replace: false });
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="nnat-page-container">
      <Header />
      <main className="nnat-main-content">
      {/* Hero Section with SEO-optimized content */}
      <section className="nnat-hero-section">
        <div className="nnat-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="nnat-hero-text"
          >
            <h1 className="nnat-main-title">Naglieri Nonverbal Ability Test (NNAT) - Complete Guide & Practice Questions</h1>
            <p className="nnat-hero-description">
              The Naglieri Nonverbal Ability Test (NNAT) is a standardized assessment designed to measure a student's nonverbal reasoning and problem-solving abilities. Unlike traditional tests, NNAT focuses on pattern recognition, spatial reasoning, and logical thinking without relying on language or mathematical skills, making it ideal for assessing students from diverse backgrounds.
            </p>
            <p className="nnat-hero-description">
              This free guide provides everything you need to understand the NNAT test format, practice with sample questions covering all four key areas, and prepare your child for success. All practice materials are completely free and designed specifically for elementary school students.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Areas Section */}
      <section className="nnat-areas-section">
        <div className="nnat-areas-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="nnat-areas-content"
          >
            <h2 className="nnat-section-title">NNAT Test Structure: Four Key Areas</h2>
            <p className="nnat-section-intro">
              The NNAT test is systematically organized into four main areas, each designed to assess different types of nonverbal reasoning abilities. Understanding these areas helps students and parents prepare effectively for the test.
            </p>

            <div className="nnat-areas-grid">
              {/* Pattern Completion */}
              <motion.div 
                className="nnat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="nnat-area-title">
                  <span className="area-icon">🧩</span>
                  Pattern Completion
                </h3>
                <p className="nnat-area-description">
                  Students identify the missing portion of a visual pattern by analyzing the existing elements and recognizing the underlying structure. This area tests visual perception and pattern recognition skills.
                </p>
              </motion.div>

              {/* Reasoning by Analogy */}
              <motion.div 
                className="nnat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="nnat-area-title">
                  <span className="area-icon">🔄</span>
                  Reasoning by Analogy
                </h3>
                <p className="nnat-area-description">
                  Students recognize relationships between shapes and complete analogies by identifying how elements relate to each other. This area assesses logical reasoning and relationship recognition.
                </p>
              </motion.div>

              {/* Serial Reasoning */}
              <motion.div 
                className="nnat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="nnat-area-title">
                  <span className="area-icon">📊</span>
                  Serial Reasoning
                </h3>
                <p className="nnat-area-description">
                  Students identify patterns in sequences and select the next shape that logically continues the series. This area tests sequential thinking and pattern extension abilities.
                </p>
              </motion.div>

              {/* Spatial Visualization */}
              <motion.div 
                className="nnat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="nnat-area-title">
                  <span className="area-icon">📐</span>
                  Spatial Visualization
                </h3>
                <p className="nnat-area-description">
                  Students mentally manipulate shapes to determine how they fit together, rotate, or transform. This area assesses spatial reasoning and mental rotation capabilities.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Test Overview Section */}
      <section className="nnat-overview-section">
        <div className="nnat-overview-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="nnat-overview-content"
          >
            <h2 className="nnat-section-title">NNAT Test Overview & Format</h2>
            <p className="nnat-overview-text">
              The Naglieri Nonverbal Ability Test (NNAT) is a standardized assessment that measures nonverbal reasoning abilities through visual-based questions. The test provides valuable insights for gifted program identification, academic placement, and understanding a student's cognitive strengths without language barriers.
            </p>
            
            <div className="nnat-test-info-grid">
              <div className="nnat-info-card">
                <h4 className="info-card-title">⏱️ Test Duration</h4>
                <p className="info-card-text">30-45 minutes total, typically administered in a single session</p>
              </div>
              <div className="nnat-info-card">
                <h4 className="info-card-title">📝 Test Format</h4>
                <p className="info-card-text">Multiple-choice questions with visual patterns and shapes, available in both paper-based and online formats</p>
              </div>
              <div className="nnat-info-card">
                <h4 className="info-card-title">📊 Scoring System</h4>
                <p className="info-card-text">Results are scored using standard age norms and percentile rankings for comparison with peers</p>
              </div>
              <div className="nnat-info-card">
                <h4 className="info-card-title">🎯 Grade Levels</h4>
                <p className="info-card-text">Typically administered to students in grades K-12, with age-appropriate question sets for each level</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sample Questions Section */}
      <NnatExamples />

      {/* FAQ Section */}
      <FAQNNAT />

      {/* Call to Action Section */}
      <section className="nnat-cta-section">
        <div className="nnat-cta-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="nnat-cta-content"
          >
            <h2 className="nnat-cta-title">Ready to Practice for the NNAT Test?</h2>
            <p className="nnat-cta-text">
              Access thousands of free NNAT practice questions covering all four key areas. Create custom practice tests, track your progress, and download printable PDFs - all completely free!
            </p>
            <div className="nnat-cta-buttons">
              <a href="/account" className="nnat-cta-button nnat-cta-primary">
                <BookOpen size={20} />
                Start Practicing Free
              </a>
              <button onClick={handleBackToHome} className="nnat-cta-button nnat-cta-secondary">
                <Home size={20} />
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
}
